import React, {useContext, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import GroupForm from '../../views/group/group-form';
import GroupTable from '../../views/group/group-table';
import useAlerts from '../../hooks/use-alerts';
import {ModalContext} from '../../apiContexts';
import useServices from '../../hooks/use-services';
import PermissionTabs from '../../views/permission';
import useGroups from '../../hooks/use-groups';
import useUsers from '../../hooks/use-users';
import useTimezone from '../../hooks/use-timezone';
import {formatDateTimeColumn} from '../../helpers/excel-helper';
import {observer} from 'mobx-react';

const permissions = ['device', 'notification', 'driver', 'geofence', 'maintenance', 'attribute'];

const noCreator = {getUserById: () => undefined};

const formatGroups = (arr, userStore = noCreator, timezone) => {
    return arr.map((item) => {
        const creator = userStore.getUserById(item.createdByUserId);
        return {
            ...item,
            creatorName: creator ? creator.name : '',
            creatorEmail: creator ? creator.email : '',
            createdAtFormatted: formatDateTimeColumn(item.createdAt, timezone),
            updatedAtFormatted: formatDateTimeColumn(item.updatedAt, timezone),
        };
    });
};

/**
 *
 * Group class container
 *
 * @return {*}
 * @constructor
 */
const GroupContainer = () => {
    const intl = useIntl();
    const {toast} = useAlerts();
    const {groupService} = useServices();
    const {showModalLoading, isModalLoading} = useContext(ModalContext);
    const selectedGroupData = useRef(null);
    const [view, setView] = useState('groupList');
    const [selectedGroup, setSelectedGroup] = useState(null);

    const {groupStore} = useGroups();
    const {users: userStore} = useUsers();
    const {timezone} = useTimezone();

    const newRegister = async (data) => {
        try {
            showModalLoading(true);
            const result = await groupService.save(data);
            groupStore.addGroup(result);
            setView('groupList');
            toast.success(intl.formatMessage({id: 'sharedSavedSuccess'}));
        } catch (e) {
            toast.errorStatus(e);
        } finally {
            showModalLoading(false);
        }
    };

    const updateRegister = async (data, afterTo = 'groupList') => {
        try {
            showModalLoading(true);
            const result = await groupService.update(data);
            const {id: groupId, ...updatedGroupData} = result;
            groupStore.updateGroup(groupId, {
                name: updatedGroupData.name,
                groupId: updatedGroupData.groupId,
                attributes: updatedGroupData.attributes,
                createdByUserId: updatedGroupData.createdByUserId,
                createdAt: updatedGroupData.createdAt,
                updatedAt: updatedGroupData.updatedAt,
            });
            setView(afterTo);
            toast.success(intl.formatMessage({id: 'sharedUpdatedSuccess'}));
            selectedGroupData.current = null;
        } catch (e) {
            toast.errorStatus(e);
        } finally {
            showModalLoading(false);
        }
    };

    const removeRegister = async (data) => {
        try {
            showModalLoading(true);
            await groupService.remove(data.id);
            groupStore.removeGroup(data.id);
            setView('groupList');
            toast.success(intl.formatMessage({id: 'sharedRemovedSuccess'}));
        } catch (e) {
            toast.errorStatus(e);
        } finally {
            showModalLoading(false);
        }
    };

    const showPermissionTabs = (data) => {
        setSelectedGroup({groupId: data.id});
        setView('groupPermissions');
    };

    const onPressAdd = () => {
        setView('groupForm');
    };

    const onPressEdit = (data) => {
        selectedGroupData.current = data;
        setView('groupForm');
    };

    const onGroupFormCancel = () => {
        setView('groupList');
        selectedGroupData.current = null;
    };

    return (
        <>
            {
                {
                    groupList: (
                        <GroupTable
                            list={formatGroups(groupStore.list, userStore, timezone)}
                            onPressAdd={onPressAdd}
                            onPressEdit={onPressEdit}
                            removeRegister={removeRegister}
                            permissions={showPermissionTabs}
                            loading={isModalLoading}
                        />
                    ),
                    groupForm: (
                        <GroupForm
                            cancel={onGroupFormCancel}
                            data={Object.assign({}, selectedGroupData.current)}
                            edit={!!selectedGroupData.current}
                            newRegister={newRegister}
                            groups={groupStore.listAsOptions}
                            updateRegister={updateRegister}
                            loading={isModalLoading}
                        />
                    ),
                    groupPermissions: (
                        <PermissionTabs
                            options={permissions}
                            data={selectedGroup}
                            cancel={() => setView('groupList')}/>
                    ),
                }[view]
            }
        </>
    );
};

export default observer(GroupContainer);
